import { useEffect, useState } from "react";
import { Trash2, Download, ExternalLink, AlertTriangle } from "lucide-react";
import { Button, Modal, Table } from "../../../components";
import axios from "axios";
import {
  SUBSCRIPTION_PAYMENTS_URL,
  BLOCKED_PAYMENTS_URL,
} from "../../../features/UrlHelper";
import { handleApiError } from "../../../features/errorHandler";

const authHeaders = () => ({
  headers: {
    Authorization: `Bearer ${window.localStorage.getItem("authToken")}`,
  },
});

function AccountingSection() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [blockedPayments, setBlockedPayments] = useState([]);
  const [blockedLoading, setBlockedLoading] = useState(true);

  useEffect(() => {
    loadPayments();
    loadBlockedPayments();
  }, []);

  function loadPayments() {
    setLoading(true);
    axios
      .get(SUBSCRIPTION_PAYMENTS_URL, authHeaders())
      .then((res) => setPayments(res.data))
      .catch((err) => handleApiError(err, "Nepodarilo sa načítať platby."))
      .finally(() => setLoading(false));
  }

  function loadBlockedPayments() {
    setBlockedLoading(true);
    axios
      .get(BLOCKED_PAYMENTS_URL, authHeaders())
      .then((res) => setBlockedPayments(res.data || []))
      .catch((err) => handleApiError(err, "Nepodarilo sa načítať blokované platby."))
      .finally(() => setBlockedLoading(false));
  }

  const getPaymentType = (row) => {
    const id = row.stripe_invoice_id ?? "";
    if (id.includes("cash") || id.includes("transfer")) return "manual";
    return "stripe";
  };

  const typeLabel = (row) => {
    const id = row.stripe_invoice_id ?? "";
    if (id.includes("cash")) return "Hotovosť";
    if (id.includes("transfer")) return "Prevod";
    return "Stripe";
  };

  const getSubStatus = (sub) => {
    if (sub.is_refunded) return "refunded";
    const now = new Date();
    const from = new Date(sub.valid_from);
    const to = new Date(sub.valid_to);
    if (now < from) return "pending";
    if (now > to) return "expired";
    return "active";
  };

  const statusLabel = (status) => {
    switch (status) {
      case "active":
        return "Aktívne";
      case "expired":
        return "Vypršané";
      case "pending":
        return "Čakajúce";
      case "refunded":
        return "Refundované";
      default:
        return status;
    }
  };

  const openReceipt = (url, row) => {
    // const isStripe = getPaymentType(row) === "stripe";

    // if (isStripe) {
    //   // Stripe receipt URL je verejný externý odkaz – otvoríme priamo
    //   window.open(url, "_blank", "noopener,noreferrer");
    // } else {
    // Manuálna platba – doklad je na našom backende, potrebuje auth
    axios
      .get(url, { ...authHeaders(), responseType: "blob" })
      .then((res) => {
        const blobUrl = URL.createObjectURL(res.data);
        window.open(blobUrl, "_blank");
      })
      .catch((err) => handleApiError(err, { showAlert: true, fallbackMessage: "Nepodarilo sa otvoriť doklad." }));
    //}
  };

  const fmt = (val) =>
    val != null ? `${(val / 100).toFixed(2)} €` : "—";

  const totalStripeFees = payments.reduce(
    (sum, p) => sum + (p.stripe_fee ?? 0),
    0
  );
  const netTotal = payments.reduce(
    (sum, p) => sum + (p.net_amount ?? 0),
    0
  );
  const totalRefunded = payments.reduce(
    (sum, p) => sum + (p.refund_amount ?? 0),
    0
  );
  const finalNet = netTotal - totalRefunded;

  return (
    <div className="admin__section">
      <h2 className="admin__section-title">Účtovníctvo</h2>

      {/* === BLOKOVANÉ / ČAKAJÚCE PLATBY === */}
      {!blockedLoading && blockedPayments.length > 0 && (
        <div
          style={{
            marginBottom: 24,
            border: "2px solid #e03131",
            borderRadius: 10,
            background: "linear-gradient(135deg, #fff5f5 0%, #ffe3e3 100%)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "14px 20px",
              background: "#e03131",
              color: "#fff",
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            <AlertTriangle size={20} />
            Blokované / čakajúce platby ({blockedPayments.length})
          </div>
          <div style={{ padding: "12px 16px" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
                gap: 12,
              }}
            >
              {blockedPayments.map((bp) => (
                <div
                  key={bp.paymentIntentId}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    padding: "14px 16px",
                    borderRadius: 8,
                    background: bp.status === "PROCESSING" ? "#fff9db" : "#fff5f5",
                    border: `1px solid ${bp.status === "PROCESSING" ? "#fcc419" : "#ff6b6b"}`,
                    fontSize: 13,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <strong style={{ fontSize: 14 }}>{bp.userName}</strong>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        padding: "3px 10px",
                        borderRadius: 20,
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        background: bp.status === "PROCESSING" ? "#fcc419" : "#ff6b6b",
                        color: bp.status === "PROCESSING" ? "#5c4813" : "#fff",
                      }}
                    >
                      {bp.status === "PROCESSING" ? "⏳" : "🛑"} {bp.statusLabel}
                    </span>
                  </div>

                  <div style={{ color: "#495057" }}>
                    <span style={{ color: "#868e96" }}>Email:</span>{" "}
                    {bp.userEmail}
                  </div>
                  <div style={{ color: "#495057" }}>
                    <span style={{ color: "#868e96" }}>Produkt:</span>{" "}
                    {bp.productName}
                  </div>
                  <div style={{ display: "flex", gap: 16 }}>
                    <div>
                      <span style={{ color: "#868e96" }}>Suma:</span>{" "}
                      <strong>{fmt(bp.amount)}</strong>
                    </div>
                    <div>
                      <span style={{ color: "#868e96" }}>Dátum:</span>{" "}
                      {bp.created
                        ? new Date(bp.created).toLocaleDateString("sk-SK", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                        : "—"}
                    </div>
                  </div>
                  <div
                    style={{
                      marginTop: 4,
                      fontFamily: "monospace",
                      fontSize: 11,
                      color: "#868e96",
                      wordBreak: "break-all",
                    }}
                  >
                    {bp.paymentIntentId}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="admin__sub-header">
        <h3 className="admin__section-subtitle">Platby</h3>
      </div>

      <Table
        columns={[
          {
            key: "payment_created_at",
            label: "Dátum",
            render: (val) => new Date(val).toLocaleDateString("sk-SK"),
          },
          {
            key: "user",
            label: "Užívateľ",
            sortable: false,
            render: (_, row) => (
              <div>
                <div className="admin__user-name">
                  {`${row.firstName || ""} ${row.lastName || ""}`.trim() ||
                    row.email}
                  {row.user_deleted ? " (zmazaný)" : ""}
                </div>
                <div className="admin__user-email">{row.email}</div>
              </div>
            ),
          },
          {
            key: "plan_name",
            label: "Plán / Suma",
            render: (val, row) => (
              <div>
                <div className="admin__user-name">{val || "—"}</div>
                <div className="admin__user-email">
                  {parseFloat(row.amount).toFixed(2)} €
                </div>
              </div>
            ),
          },
          {
            key: "valid_from",
            label: "Platnosť",
            render: (val, row) => (
              <div>
                <div className="admin__user-name">
                  {val ? new Date(val).toLocaleDateString("sk-SK") : "—"}
                </div>
                <div className="admin__user-email">
                  {row.valid_to
                    ? new Date(row.valid_to).toLocaleDateString("sk-SK")
                    : "—"}
                </div>
              </div>
            ),
          },
          {
            key: "type",
            label: "Typ / Stav",
            sortable: false,
            render: (_, row) => {
              const type = getPaymentType(row);
              return (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  <span
                    className={`admin__type-badge admin__type-badge--${type}`}
                  >
                    {typeLabel(row)}
                  </span>

                  <span
                    className={`admin__status-badge admin__status-badge--${getSubStatus(row)}`}
                  >
                    {statusLabel(getSubStatus(row))}
                  </span>
                </div>
              );
            },
          },
          {
            key: "finances",
            label: "Stripe fee / Net / Refund / Final",
            sortable: false,
            render: (_, row) => {
              const fee = row.stripe_fee != null ? fmt(row.stripe_fee) : "—";
              const net = row.net_amount != null ? fmt(row.net_amount) : "—";
              const refund = row.refund_amount != null ? fmt(row.refund_amount) : "—";
              const rowFinalNet = row.final_net != null ? fmt(row.final_net) : "—";
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 12 }}>
                  <span><span className="admin__user-email">Fee:</span> {fee}</span>
                  <span><span className="admin__user-email">Net:</span> {net}</span>
                  <span><span className="admin__user-email">Refund:</span> {refund}</span>
                  <span><span className="admin__user-email">Final:</span> <strong>{rowFinalNet}</strong></span>
                </div>
              );
            },
          },
          {
            key: "stripe_invoice_id",
            label: "Transakcia / Doklad",
            sortable: false,
            render: (val, row) => (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {val ? (
                  <span
                    className="admin__cell--truncate"
                    title={val}
                    style={{ maxWidth: 140 }}
                  >
                    {val}
                  </span>
                ) : (
                  <span className="admin__locked">—</span>
                )}
                {row.invoice_number && (
                  <span className="admin__user-email" title="Číslo faktúry">
                    {row.invoice_number}
                  </span>
                )}
                {row.dobropis_number && (
                  <span className="admin__user-email" title="Číslo dobropisu">
                    {row.dobropis_number}
                  </span>
                )}
                {row.doklad ? (
                  <button
                    className="admin__receipt-link"
                    title="Otvoriť doklad"
                    onClick={() => openReceipt(row.doklad, row)}
                  >
                    <ExternalLink size={13} /> Doklad
                  </button>
                ) : (
                  <span className="admin__locked" style={{ fontSize: 12 }}>
                    Bez dokladu
                  </span>
                )}
              </div>
            ),
          },
          {
            key: "actions",
            label: "Akcie",
            sortable: false,
            render: (_, row) =>
              !row.stripe_invoice_id ? (
                <button
                  className="admin__icon-btn admin__icon-btn--danger"
                  onClick={() => setDeleteConfirm(row.payment_id)}
                  title="Zmazať"
                >
                  <Trash2 size={16} />
                </button>
              ) : (
                <span className="admin__locked">—</span>
              ),
          },
        ]}
        data={payments}
        searchPlaceholder="Hľadať platbu..."
        emptyMessage={loading ? "Načítavam..." : "Žiadne platby"}
      />
    </div>
  );
}

export default AccountingSection;
